/**
 * @file dialog.tsx
 *
 * Dialog component system built on Material Design 3 Expressive guidelines.
 *
 * Extends Radix UI Dialog primitives with Framer Motion physics-based Spring animations
 * for entrance and exit transitions matching MD3 Expressive specifications.
 * Supports both Standard modal and Full-Screen dialog variants.
 *
 * @see https://m3.material.io/components/dialogs/overview
 */

import * as RadixDialog from "@radix-ui/react-dialog";
import { AnimatePresence, m } from "motion/react";
import * as React from "react";
import { cn } from "../lib/utils";
import { IconButton } from "./buttons/icon-button";
import { Icon } from "./icon";
import { ScrollArea } from "./scroll-area";
import { DEFAULT_SPATIAL_SPRING } from "./shared/motion-tokens";

// ─── MD3 Spring Config (Expressive) ──────────────────────────────────────────
// MD3 default.spatial: stiffness=380, dampingRatio=0.8 → Framer damping ≈ 31.19
const MD3_SPRING = DEFAULT_SPATIAL_SPRING;

const MD3_OVERLAY_ANIM = {
	initial: { opacity: 0 },
	animate: {
		opacity: 1,
		transition: { duration: 0.2, ease: "easeOut" as const },
	},
	exit: { opacity: 0, transition: { duration: 0.15, ease: "easeIn" as const } },
};

const MD3_CONTENT_ANIM = {
	initial: { opacity: 0, scale: 0.85, y: 24 },
	animate: { opacity: 1, scale: 1, y: 0, transition: MD3_SPRING },
	exit: {
		opacity: 0,
		scale: 0.95,
		y: 8,
		transition: { duration: 0.15, ease: "easeIn" as const },
	},
};

const MD3_FULLSCREEN_ANIM = {
	initial: { y: "100%" },
	animate: { y: 0, transition: MD3_SPRING },
	exit: { y: "100%", transition: { duration: 0.2, ease: "easeIn" as const } },
};

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Root `Dialog` props. Wraps Radix `Dialog.Root` supporting controlled and uncontrolled open state.
 *
 * @example
 * ```tsx
 * const [open, setOpen] = React.useState(false);
 *
 * <Dialog open={open} onOpenChange={setOpen}>
 *   <DialogTrigger asChild>
 *      <Button>Open Dialog</Button>
 *   </DialogTrigger>
 *   <DialogContent>
 *     <DialogHeader>
 *        <DialogTitle>Confirm Action</DialogTitle>
 *     </DialogHeader>
 *     <DialogBody>Are you sure you want to proceed?</DialogBody>
 *     <DialogFooter>
 *        <Button onClick={() => setOpen(false)} variant="text">Cancel</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 * ```
 */
export interface DialogProps {
	/** Whether the dialog is open (controlled). Omit for uncontrolled. */
	open?: boolean;
	/** Called when the open state should change. */
	onOpenChange?: (open: boolean) => void;
	/** Dialog trigger + content. */
	children: React.ReactNode;
}

/**
 * Props passed to standard container `DialogContent`.
 *
 * @see {@link DialogContent}
 */
export interface DialogContentProps
	extends React.ComponentPropsWithoutRef<typeof RadixDialog.Content> {
	/** Hides the top-right close (X) button. @default false */
	hideCloseButton?: boolean;
	className?: string;
}

/**
 * Props for `DialogFullScreenContent` dedicated to Full-Screen dialog mode.
 *
 * @remarks
 * Full-screen dialogs expand to cover the entire device screen and feature a Top App Bar
 * with a title label, close button, and action confirmation button.
 *
 * @see {@link DialogFullScreenContent}
 * @see https://m3.material.io/components/dialogs/guidelines#full-screen
 */
export interface DialogFullScreenContentProps
	extends React.ComponentPropsWithoutRef<typeof RadixDialog.Content> {
	/** Title label displayed in the Top App Bar. */
	title?: string;
	/** Label for the primary action button on the Top App Bar (e.g. "Save"). */
	actionLabel?: string;
	/** Handler invoked when pressing the primary action button. */
	onAction?: () => void;
	/** Renders a divider separating Top App Bar from scrollable body content. @default false */
	showDivider?: boolean;
	className?: string;
}

// ─── Re-exports wrapper ───────────────────────────────────────────────────────

/**
 * Dialog component Root — wraps Radix `Dialog.Root`.
 *
 * @remarks Supports controlled state via `open`/`onOpenChange`, or uncontrolled state using `DialogTrigger`.
 */
const Dialog = ({ open, onOpenChange, children }: DialogProps) => (
	<RadixDialog.Root open={open} onOpenChange={onOpenChange}>
		{children}
	</RadixDialog.Root>
);
Dialog.displayName = "Dialog";

/** Trigger element that toggles the open state of the Dialog (supports `asChild`). */
const DialogTrigger = RadixDialog.Trigger;
DialogTrigger.displayName = "DialogTrigger";

// ─── Portal + Overlay + Content ───────────────────────────────────────────────
const DialogPortal = ({
	open,
	children,
}: {
	open?: boolean;
	children: React.ReactNode;
}) => (
	<RadixDialog.Portal forceMount>
		<AnimatePresence>
			{open ? React.Children.toArray(children) : null}
		</AnimatePresence>
	</RadixDialog.Portal>
);
DialogPortal.displayName = "DialogPortal";

const DialogOverlay = React.forwardRef<
	React.ComponentRef<typeof RadixDialog.Overlay>,
	React.ComponentPropsWithoutRef<typeof RadixDialog.Overlay>
>(({ className, ...props }, ref) => (
	<RadixDialog.Overlay ref={ref} asChild {...props}>
		<m.div
			className={cn("fixed inset-0 z-50 bg-black/32", className)}
			aria-hidden="true"
			{...MD3_OVERLAY_ANIM}
		/>
	</RadixDialog.Overlay>
));
DialogOverlay.displayName = "DialogOverlay";

const DialogContent = React.forwardRef<
	React.ComponentRef<typeof RadixDialog.Content>,
	DialogContentProps
>(({ className, children, hideCloseButton = false, ...props }, ref) => (
	<RadixDialog.Content
		ref={ref}
		asChild
		aria-describedby={undefined}
		{...props}
	>
		<m.div
			className={cn(
				"fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
				"w-[calc(100%-2rem)] max-w-140",
				"rounded-[28px] bg-m3-surface-container-high p-6",
				"shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-m3-primary",
				className,
			)}
			role="dialog"
			{...MD3_CONTENT_ANIM}
		>
			{children}
			{!hideCloseButton && (
				<RadixDialog.Close asChild aria-label="Close dialog">
					<IconButton
						size="sm"
						colorStyle="filled"
						className="absolute right-4 top-4"
						aria-label="Close"
					>
						<Icon name="close" aria-hidden="true" />
					</IconButton>
				</RadixDialog.Close>
			)}
		</m.div>
	</RadixDialog.Content>
));
DialogContent.displayName = "DialogContent";

// ─── Header & Related ────────────────────────────────────────────────────────
const DialogIcon = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("flex justify-center mb-4 text-m3-secondary", className)}
		aria-hidden="true"
		{...props}
	>
		{children}
	</div>
));
DialogIcon.displayName = "DialogIcon";

const DialogHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn("flex flex-col gap-2 mb-4", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

// RadixDialog.Title surfaces to the accessibility tree — required
const DialogTitle = React.forwardRef<
	React.ComponentRef<typeof RadixDialog.Title>,
	React.ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(({ className, asChild, ...props }, ref) => (
	<RadixDialog.Title
		ref={ref}
		asChild={asChild}
		className={cn(
			"text-[24px] leading-8 font-normal text-m3-on-surface tracking-[0em]",
			className,
		)}
		{...props}
	/>
));
DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef<
	React.ComponentRef<typeof RadixDialog.Description>,
	React.ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(({ className, asChild, ...props }, ref) => (
	<RadixDialog.Description
		ref={ref}
		asChild={asChild}
		className={cn("text-sm text-m3-on-surface-variant leading-5", className)}
		{...props}
	/>
));
DialogDescription.displayName = "DialogDescription";

// ─── Body & Footer ───────────────────────────────────────────────────────────
const DialogBody = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, children, dir, ...props }, ref) => (
	<ScrollArea
		ref={ref}
		type="hover"
		dir={dir as "ltr" | "rtl" | undefined}
		className={cn("max-h-[calc(85dvh-200px)] -mx-6", className)}
		viewportClassName="px-6"
		{...props}
	>
		{children}
	</ScrollArea>
));
DialogBody.displayName = "DialogBody";

const DialogFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn("flex flex-row justify-end gap-2 mt-6", className)}
		{...props}
	/>
);
DialogFooter.displayName = "DialogFooter";

const DialogClose = RadixDialog.Close;

// ─── Full Screen Content Variant ──────────────────────────────────────────────
const DialogFullScreenContent = React.forwardRef<
	React.ComponentRef<typeof RadixDialog.Content>,
	DialogFullScreenContentProps
>(
	(
		{
			className,
			children,
			title,
			actionLabel,
			onAction,
			showDivider,
			...props
		},
		ref,
	) => (
		<RadixDialog.Content
			ref={ref}
			asChild
			aria-describedby={undefined}
			{...props}
		>
			<m.div
				className={cn(
					"fixed inset-0 z-50 w-full h-full bg-m3-surface flex flex-col",
					"outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-m3-primary",
					className,
				)}
				role="dialog"
				{...MD3_FULLSCREEN_ANIM}
			>
				<div className="flex shrink-0 items-center px-4 h-14 gap-2 bg-m3-surface">
					<RadixDialog.Close asChild aria-label="Close dialog">
						<IconButton size="sm" colorStyle="filled" aria-label="Close">
							<Icon name="close" aria-hidden="true" />
						</IconButton>
					</RadixDialog.Close>

					{title && (
						<DialogTitle className="flex-1 text-[22px] leading-7 font-medium truncate pr-2">
							{title}
						</DialogTitle>
					)}

					{actionLabel && onAction && (
						<button
							type="button"
							onClick={onAction}
							className="text-sm font-medium text-m3-primary px-3 py-2 rounded-full hover:bg-m3-primary/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-m3-primary transition-colors whitespace-nowrap"
						>
							{actionLabel}
						</button>
					)}
				</div>

				{showDivider && (
					<hr className="border-m3-outline-variant w-full shrink-0 m-0" />
				)}

				<ScrollArea
					type="hover"
					className="flex-1 w-full"
					viewportClassName="p-6"
				>
					{children}
				</ScrollArea>
			</m.div>
		</RadixDialog.Content>
	),
);
DialogFullScreenContent.displayName = "DialogFullScreenContent";

export {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogFullScreenContent,
	DialogHeader,
	DialogIcon,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
};
